import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import tokens from "../ChatWorkspace/workspaceTokens";

const accentColor = tokens.accent;
const accentText = tokens.onAccent;
const mutedText = tokens.muted;
const bodyText = tokens.text;
const borderColor = tokens.border;
const panelColor = tokens.panelSoft;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "calc(100vw - 24px)", sm: 452 },
  maxWidth: 452,
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  background: tokens.attentionGradient,
  color: tokens.text,
  borderRadius: `${tokens.radii.lg}px`,
  border: `1px solid ${tokens.strongBorder}`,
  boxShadow: `${tokens.shadows.raised}, ${tokens.innerHighlight}`,
  fontFamily: tokens.fontFamily.ui,
  outline: "none",
};

const ServiceTipPromptModal = ({
  open,
  prompt,
  onClose,
  onDismiss,
  onShown,
  onCheckout,
  onPaymentMethodStatus,
  onSavedPaymentTip,
}) => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState({ available: false });
  const [fallbackCheckoutUrl, setFallbackCheckoutUrl] = useState(null);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState(null);

  const options = useMemo(() => {
    const amounts = prompt?.amountOptionsMinorUnits || [];
    const labels = prompt?.amountOptionLabels || [];
    return amounts.map((amount, index) => ({
      amount,
      label: labels[index] || `${prompt?.currency?.toUpperCase() || ""} ${amount}`,
      recommended: amount === prompt?.recommendedAmountMinorUnits,
    }));
  }, [prompt]);

  const selectedOption = useMemo(
    () => options.find((option) => option.amount === selectedAmount) || options[0],
    [options, selectedAmount],
  );

  useEffect(() => {
    if (open && prompt?.id && typeof onShown === "function") {
      onShown(prompt.id);
    }
  }, [open, prompt?.id, onShown]);

  useEffect(() => {
    if (!open || !prompt?.id) return;

    const defaultOption =
      options.find((option) => option.recommended) ||
      options[Math.min(1, options.length - 1)] ||
      options[0];
    setSelectedAmount(defaultOption?.amount || null);
    setError(null);
    setFallbackCheckoutUrl(null);
    setPaid(false);
  }, [open, prompt?.id, options]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !prompt?.id) return undefined;

    if (typeof onPaymentMethodStatus !== "function") {
      setPaymentMethod({ available: false });
      return undefined;
    }

    setPaymentMethodLoading(true);
    setPaymentMethod({ available: false });
    onPaymentMethodStatus(prompt.id)
      .then((result) => {
        if (!cancelled) {
          setPaymentMethod(result || { available: false });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPaymentMethod({ available: false, error: "Could not check saved card." });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPaymentMethodLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, prompt?.id, onPaymentMethodStatus]);

  const handleClose = async () => {
    if (!paid && prompt?.id && typeof onDismiss === "function") {
      await onDismiss(prompt.id);
    }
    onClose();
  };

  const redirectToCheckout = (checkoutUrl) => {
    window.location.href = checkoutUrl;
  };

  const handleCheckout = async (amount = selectedAmount) => {
    if (!amount || typeof onCheckout !== "function") return;

    setError(null);
    setCheckoutLoading(true);
    try {
      const result = await onCheckout(prompt.id, amount);
      if (result?.checkoutUrl) {
        redirectToCheckout(result.checkoutUrl);
        return;
      }
      setError(result?.error || "Could not start checkout.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAmount || !prompt?.id) return;

    setError(null);
    setFallbackCheckoutUrl(null);

    if (!paymentMethod?.available || typeof onSavedPaymentTip !== "function") {
      await handleCheckout(selectedAmount);
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSavedPaymentTip(prompt.id, selectedAmount);
      if (result?.status === "paid") {
        setPaid(true);
        return;
      }

      if (result?.checkoutUrl) {
        setFallbackCheckoutUrl(result.checkoutUrl);
      }
      setError(result?.error || "The saved card could not be charged.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseDifferentCard = () => {
    if (fallbackCheckoutUrl) {
      redirectToCheckout(fallbackCheckoutUrl);
      return;
    }
    handleCheckout(selectedAmount);
  };

  if (!prompt) return null;

  const busy = submitting || checkoutLoading;
  const savedCardAvailable = Boolean(paymentMethod?.available);
  const methodCopy = paymentMethodLoading
    ? "Checking saved card..."
    : savedCardAvailable
      ? `Using ${paymentMethod.label || "saved card"}`
      : "Secure checkout. Card saved for future tips.";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="service-tip-title"
      aria-describedby="service-tip-body"
      data-testid="service-tip-modal"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(8, 10, 14, 0.58)",
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
          },
        },
      }}
    >
      <Box data-testid="service-tip-card" sx={modalStyle}>
        <IconButton
          aria-label="Close tip prompt"
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            borderRadius: `${tokens.radii.sm}px`,
            color: tokens.muted,
            bgcolor: tokens.panelSoft,
            border: `1px solid ${tokens.border}`,
            "&:hover": {
              bgcolor: tokens.raised,
              color: tokens.text,
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box sx={{ p: { xs: 2.25, sm: 2.75 } }}>
          {paid ? (
            <Box sx={{ textAlign: "center", px: { xs: 0, sm: 1 }, py: 1.25 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: `${tokens.radii.md}px`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: tokens.accentSoft,
                  border: `1px solid ${tokens.accentBorder}`,
                  color: accentColor,
                  boxShadow: tokens.innerHighlight,
                  mb: 1.6,
                }}
              >
                <CheckCircleRoundedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography
                id="service-tip-title"
                variant="h6"
                sx={{ fontSize: 22, fontWeight: tokens.weights.bold, lineHeight: 1.16, letterSpacing: "-0.02em", mb: 0.7 }}
              >
                Tip received
              </Typography>
              <Typography id="service-tip-body" sx={{ color: mutedText, fontSize: 14, lineHeight: 1.5, mb: 2.2 }}>
                Thanks for supporting Incite.
              </Typography>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  minHeight: 46,
                  borderRadius: `${tokens.radii.md}px`,
                  bgcolor: accentColor,
                  color: accentText,
                  fontWeight: tokens.weights.semibold,
                  textTransform: "none",
                  px: 3,
                  boxShadow: tokens.shadows.card,
                  "&:hover": { bgcolor: tokens.accentHover },
                }}
              >
                Done
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.35, pr: 4.5, mb: 1.1 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: `${tokens.radii.md}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    bgcolor: tokens.accentSoft,
                    border: `1px solid ${tokens.accentBorder}`,
                    color: accentColor,
                    boxShadow: tokens.innerHighlight,
                  }}
                >
                  <PaidRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: tokens.accentText,
                      fontSize: tokens.sizes.microLabel,
                      fontWeight: tokens.weights.semibold,
                      letterSpacing: tokens.letterSpacing.label,
                      lineHeight: 1.2,
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    Optional tip
                  </Typography>
                  <Typography
                    id="service-tip-title"
                    variant="h6"
                    sx={{
                      fontSize: { xs: 20, sm: 22 },
                      fontWeight: tokens.weights.bold,
                      lineHeight: 1.16,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {prompt.headline}
                  </Typography>
                </Box>
              </Box>

              <Typography id="service-tip-body" sx={{ fontSize: 14, lineHeight: 1.5, color: mutedText, mb: 1.8 }}>
                {prompt.body}
              </Typography>

              <Box sx={{ mb: 0.9, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography sx={{ color: bodyText, fontSize: 12.5, fontWeight: tokens.weights.semibold, lineHeight: 1.2 }}>
                  Choose an amount
                </Typography>
                <Typography sx={{ color: tokens.subtle, fontSize: 11.5, lineHeight: 1.2 }}>
                  Subscription unchanged
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: { xs: 0.75, sm: 1 } }}>
                {options.map((option) => {
                  const selected = selectedAmount === option.amount;
                  return (
                    <Button
                      key={option.amount}
                      variant="outlined"
                      aria-pressed={selected}
                      disabled={busy}
                      onClick={() => setSelectedAmount(option.amount)}
                      sx={{
                        minHeight: 62,
                        px: { xs: 0.75, sm: 1.25 },
                        borderRadius: `${tokens.radii.md}px`,
                        borderWidth: 1,
                        borderColor: selected ? tokens.accentBorder : borderColor,
                        bgcolor: selected ? tokens.accentSoft : tokens.panel,
                        color: tokens.text,
                        textTransform: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.25,
                        boxShadow: selected ? `${tokens.shadows.card}, ${tokens.innerHighlight}` : tokens.innerHighlight,
                        "&:hover": {
                          borderColor: accentColor,
                          bgcolor: selected ? tokens.accentSoft : tokens.panelSoft,
                        },
                      }}
                    >
                      <Typography sx={{ ...tokens.tnum, fontSize: { xs: 15, sm: 16 }, fontWeight: tokens.weights.bold, lineHeight: 1.12 }}>
                        {option.label}
                      </Typography>
                      <Box sx={{ minHeight: 11.5, display: "flex", alignItems: "center" }}>
                        {option.recommended ? (
                          <Typography sx={{ fontSize: 10.5, fontWeight: tokens.weights.semibold, lineHeight: 1.1, color: tokens.accentText }}>
                            Suggested
                          </Typography>
                        ) : null}
                      </Box>
                    </Button>
                  );
                })}
              </Box>

              <Box
                sx={{
                  mt: 1.25,
                  px: 1.25,
                  py: 1.05,
                  borderRadius: `${tokens.radii.md}px`,
                  border: `1px solid ${borderColor}`,
                  bgcolor: panelColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.25,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <CreditCardRoundedIcon sx={{ fontSize: 18, color: tokens.accentText, flex: "0 0 auto" }} />
                  <Typography sx={{ color: mutedText, fontSize: 12.5, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                    {methodCopy}
                  </Typography>
                </Box>
                {paymentMethodLoading && <CircularProgress size={17} sx={{ color: accentColor, flex: "0 0 auto" }} />}
              </Box>

              {error && (
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.15,
                    borderRadius: `${tokens.radii.md}px`,
                    bgcolor: tokens.panelSoft,
                    border: `1px solid ${tokens.red}`,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.9,
                  }}
                >
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: tokens.red, mt: 0.05 }} />
                  <Typography sx={{ color: tokens.red, fontSize: 12.5, lineHeight: 1.4 }}>
                    {error}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 1.35 }}>
                <Button
                  variant="contained"
                  disabled={busy || paymentMethodLoading || !selectedOption}
                  onClick={handleSubmit}
                  sx={{
                    minHeight: 48,
                    width: "100%",
                    borderRadius: `${tokens.radii.md}px`,
                    bgcolor: accentColor,
                    color: accentText,
                    fontWeight: tokens.weights.semibold,
                    textTransform: "none",
                    boxShadow: tokens.shadows.card,
                    "&:hover": { bgcolor: tokens.accentHover },
                  }}
                >
                  {busy
                    ? (submitting ? "Submitting..." : "Opening checkout...")
                    : savedCardAvailable
                      ? `Submit ${selectedOption?.label || ""} tip`
                      : `Continue with ${selectedOption?.label || ""}`}
                </Button>
              </Box>
              <Box
                sx={{
                  minHeight: 38,
                  mt: 0.45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.25,
                }}
              >
                {(savedCardAvailable || fallbackCheckoutUrl) ? (
                  <Button
                    variant="text"
                    disabled={busy}
                    onClick={handleUseDifferentCard}
                    sx={{
                      minHeight: 36,
                      color: mutedText,
                      fontWeight: tokens.weights.medium,
                      textTransform: "none",
                      px: 1.1,
                      "&:hover": {
                        bgcolor: tokens.panelSoft,
                        color: tokens.text,
                      },
                    }}
                  >
                    Use another card
                  </Button>
                ) : null}
                {(savedCardAvailable || fallbackCheckoutUrl) ? (
                  <Typography aria-hidden="true" sx={{ color: tokens.subtle, fontSize: 13 }}>
                    ·
                  </Typography>
                ) : null}
                <Button
                  variant="text"
                  disabled={busy}
                  onClick={handleClose}
                  sx={{
                    minHeight: 36,
                    color: mutedText,
                    fontWeight: tokens.weights.medium,
                    textTransform: "none",
                    px: 1.1,
                    "&:hover": {
                      bgcolor: tokens.panelSoft,
                      color: tokens.text,
                    },
                  }}
                >
                  Not now
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ServiceTipPromptModal;
